import Container from '@mui/material/Container';
// import Grid from "@mui/material/Grid";
import Grid from '@mui/material/Unstable_Grid2';
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DeleteIcon from '@mui/icons-material/Delete';
import { Dayjs } from 'dayjs';

import {
  GridColDef,
} from '@mui/x-data-grid';

import useModal from '../../hooks/use-modal';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Filter } from '../../components/common/filter';
import { CustomDataGrid } from '../../components/common/datagrid';
import { articleStyle } from '../../styles/pages/article/article';

import { Article } from '../../types/article'
import { useQueryArticle, useMutationDeleteArticle } from '../../api/article';
import { selectAuthUser } from '../../store/auth/selector';

interface ActionsProps {
  articleId: string
  deleteParams: (id: string) => void;
}

function Actions({ deleteParams, articleId }: ActionsProps){
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
      aria-label="more"
      id="long-button"
      aria-controls={open ? 'long-menu' : undefined}
      aria-expanded={open ? 'true' : undefined}
      aria-haspopup="true"
      onClick={handleClick}
    >
      <MoreHorizIcon />
      </IconButton>
      <Menu
        id="basic-menu"
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={() => deleteParams(articleId)}>
          <ListItemIcon sx={articleStyle.dataGridMenuMenuItemListItemIcon}>
            <DeleteIcon />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>
    </>
  );
}

export function ArticleList() {
  const { showModal } = useModal();
  const navigate = useNavigate();
  const profile = useSelector(selectAuthUser);
  const queryClient = useQueryClient();

  const [startDateValue, setStartDateValue] = useState<Dayjs | null>(null);
  const [EndDatevalue, setEndDateValue] = useState<Dayjs | null>(null);
  const [searchResult, setSearchResult] = useState('');
  const [totalItems, setTotalItems] = useState(0)
  const [paginationModel, setPaginationModel] = useState({pageSize: 10, page: 0});
  const [articleList, setArticleList] = useState<Article[]>([])

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Article Name',
      flex: 1,
      minWidth: 200,
      hideable: false,
      renderCell: (params) => (
        <>
          <Box 
            component="img" 
            src={params.row.thumbnail}
            alt="img-art"
            sx={articleStyle.dataGridBox}
          />
          <Link tabIndex={params.tabIndex} color="black" underline="hover" onClick={() => navigate(`/article-detail/${params.row.articleId}`)}>{params.row.title}</Link>
        </>
      ),
    },
    {
      field: 'publisher',
      headerName: 'Publisher',
      flex: 0.5,
      minWidth: 100,
      hideable: false,
    },
    {
      field: 'publishedDate',
      headerName: 'Published Date',
      flex: 0.5,
      minWidth: 100,
      hideable: false,
    },
    // {
    //   field: 'projectId',
    //   headerName: 'Project ID',
    //   flex: 0.5,
    //   minWidth: 100,
    //   hideable: false,
    // },
    // {
    //   field: 'projectName',
    //   headerName: 'Project Name',
    //   flex: 0.5,
    //   minWidth: 100,
    //   hideable: false,
    // },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      align: 'center',
      hideable: false,
      renderCell: (params) => <Actions deleteParams={deleteProjectConfirmation} articleId={params.row.articleId}/>
    }, 
  ];

  const getArticle = useQueryArticle({
    query: {
      search: searchResult,
      take: paginationModel.pageSize,
      page: paginationModel.page,
      startDate: startDateValue ? startDateValue?.format('YYYY-MM-DD') : "",
      endDate: EndDatevalue ? EndDatevalue?.format('YYYY-MM-DD') : ""
    },
    options: {
      onSuccess: (res) => {
        setArticleList(res.data.articles);
        setTotalItems(res.data.totalItems);
      },
      onError: () => {
        setArticleList([]);
      }
    },
  });

  const deleteArticle = useMutationDeleteArticle({
    onSettled: async (res) => {
      if (res) {
        queryClient.invalidateQueries({ queryKey: ['article'] });
      }
    },
    onSuccess: async (res) => {
      showModal({
        title: 'Success',
        message:
          'Delete article successfully.',
        options: {
          variant: 'success',
          onOk: () => {
            queryClient.invalidateQueries({ queryKey: ['article'] });
          }
        },
      });
    },
    onError: async (error) => {
      showModal({
        title: 'Error',
        message:
          'Delete article is failed.',
        options: {
          variant: 'failed',
        },
      });
    },
  })

  const deleteProjectConfirmation = (id: string) => {
    showModal({
      title: 'Attention',
      message:
        'Are you sure you want to delete this item?',
      options: {
        variant: 'info',
        buttonTitle: 'Yes',
        buttonTitle2: 'No',
        onOk: () => {
          deleteArticle.mutate({
            userId: profile.userId,
            articleId: id
          });
        },
      },
    });
  }

  useEffect(() => {
    if(searchResult === null || searchResult === ""){
      getArticle.refetch()
    }

  }, [searchResult])

  return (
    <Container maxWidth={false} sx={articleStyle.contentContainer}>
      <Grid container spacing={2} sx={articleStyle.contentContainerGrid}>
        <Grid xs={3}>
          <Typography variant="h3" sx={articleStyle.contentContainerGridItem1Typography}>Article</Typography>
        </Grid>
        <Grid container spacing={2} xs sx={articleStyle.contentContainerGrid}>
          <Filter 
            placeholder="Search Article" 
            onSearchChange={(searchTerm) => setSearchResult(searchTerm)} 
            onSearchClick={() => getArticle.refetch()} 
            startDate={(startDate) => setStartDateValue(startDate)}
            endDate={(endDate) => setEndDateValue(endDate)}
          />
        </Grid>
        <Grid xs={12}>
          <Paper elevation={3} sx={articleStyle.contentContainerGridItem2Paper}>
            {getArticle.isLoading || getArticle.isFetching ? 
              (<Skeleton variant="rounded" width="auto" height="80vh" />) : (
                <CustomDataGrid
                  columnsData={columns}
                  rowsData={articleList}
                  rowCount={totalItems}
                  getRowId={(row) => row.articleId}
                  setPaginationModel={(value) => {
                    setPaginationModel(value);
                  }}
                  paginationModel={paginationModel}
                  columnVisibilityModel={{ actions: profile.role === "Admin"}}
                />  
              )
            }
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}